/** IoT Mock数据
 *
 * TODO: remove when API ready — all mock functions are superseded by real API calls
 * in bluplanet-admin/src/api/iot.ts. Keep for dev/storybook fallback until prod cutover.
 */

import type { IoTDevice, IoTAlert, SensorReading, IoTDeviceStats, GISRouteData, TempChartDataPoint } from '../types';
import { IoTDeviceType, IoTDeviceStatus, AlertType, AlertSeverity, TimeRange } from '../types';
import { randomFloat, randomPick, randomTimestamp } from '../utils/mockHelper';

/** 温度阈值常量 */
const TEMP_UPPER = 4;
const TEMP_LOWER = 0;
const TEMP_TARGET = -3;

function generateSensorReading(hoursAgo: number, forceHighTemp: boolean = false): SensorReading {
  const timestamp = new Date(Date.now() - hoursAgo * 3600000).toISOString();
  const baseTemp = forceHighTemp ? randomFloat(3.5, 6.5, 1) : randomFloat(1.5, 4.5, 1);
  return {
    timestamp,
    temperature: baseTemp,
    humidity: randomFloat(50, 80, 1),
    co2: randomFloat(380, 500, 0),
    shock: randomFloat(0, 0.5, 1),
    lightExposure: randomFloat(2, 10, 0),
  };
}

function generateReadingHistory(forceHighTemp: boolean = false): SensorReading[] {
  return Array.from({ length: 192 }, (_, i) => generateSensorReading(192 - i, forceHighTemp));
}

/** 生成指定状态的设备 */
function generateIoTDevice(index: number, status: IoTDeviceStatus, batteryLevel?: number): IoTDevice {
  const types = [IoTDeviceType.TEMP_HUMIDITY, IoTDeviceType.GPS_TRACKER, IoTDeviceType.SHOCK_SENSOR, IoTDeviceType.MULTI_SENSOR];
  // 温度报警设备：index 为 5, 15, 25 的设备有高温读数
  const isTempAlertDevice = [5, 15, 25].includes(index);
  const history = generateReadingHistory(isTempAlertDevice);

  // 确保近48个读数中有一些超过阈值（触发报警的3台设备）
  if (isTempAlertDevice) {
    // 修改最近的几个读数使其超过阈值
    for (let i = history.length - 15; i < history.length; i++) {
      if (i % 3 === 0) {
        history[i] = { ...history[i], temperature: randomFloat(4.1, 6.8, 1) };
      }
    }
    // 确保最近一次读数超过阈值
    history[history.length - 1] = { ...history[history.length - 1], temperature: randomFloat(4.5, 6.8, 1) };
  }

  return {
    id: `DEV${String(index + 1).padStart(3, '0')}`,
    deviceNo: `IOT-BP-${2001 + index}`,
    type: types[index % 4],
    containerId: `CONT${String(index + 1).padStart(3, '0')}`,
    containerNo: `MSCU-${String(8000000 + index * 13).padStart(7, '0')}`,
    lastReading: history[history.length - 1],
    readingHistory: history,
    batteryLevel: batteryLevel ?? randomFloat(30, 100, 0),
    signalStrength: randomFloat(60, 100, 0),
    status,
    installedAt: randomTimestamp(90),
  };
}

/** 生成全部 47 台 IoT 设备：
 *  - 42 台 ONLINE
 *  - 3 台 OFFLINE
 *  - 2 台 LOW_BATTERY (batteryLevel < 20%)
 *  其中 3 台 ONLINE 设备有高温读数（关联温度报警）
 */
function generateAllDevices(): IoTDevice[] {
  const devices: IoTDevice[] = [];

  for (let i = 0; i < 47; i++) {
    if (i < 42) {
      // ONLINE: 42台
      devices.push(generateIoTDevice(i, IoTDeviceStatus.ONLINE));
    } else if (i < 45) {
      // OFFLINE: 3台 (index 42-44)
      devices.push(generateIoTDevice(i, IoTDeviceStatus.OFFLINE));
    } else {
      // LOW_BATTERY: 2台 (index 45-46)
      devices.push(generateIoTDevice(i, IoTDeviceStatus.LOW_BATTERY, randomFloat(5, 19, 0)));
    }
  }

  return devices;
}

export const mockIoTDevices: IoTDevice[] = generateAllDevices();

/** 设备统计数据（对齐 PRD：47/44/3/93.6/3/2） */
export const mockIoTDeviceStats: IoTDeviceStats = {
  totalDevices: 47,
  onlineDevices: 44,
  offlineDevices: 3,
  onlineRate: 93.6,
  tempAlertCount: 3,
  batteryWarningCount: 2,
};

/** GIS 航线数据 — 青岛 → 迪拜固定航线 */
export const mockGisRouteData: GISRouteData = {
  routeId: 'ROUTE-QD-DXB',
  routeName: '青岛 → 迪拜',
  points: [
    { name: '青岛', lat: 36.06, lng: 120.38, label: 'QD', isCurrent: false },
    { name: '当前位置', lat: 25.3, lng: 62.5, label: 'POS', isCurrent: true },
    { name: '迪拜', lat: 25.2, lng: 55.27, label: 'DXB', isCurrent: false },
  ],
  sailedNauticalMiles: 3240,
  remainingNauticalMiles: 2160,
  estimatedArrival: new Date(Date.now() + 12 * 86400000).toISOString(),
  progressPercent: 60,
};

/** 根据设备动态生成 GIS 航线数据（随设备切换联动） */
export function getDeviceGisRoute(device: IoTDevice): GISRouteData {
  // 防御式处理：设备为空或缺少 id 时返回兜底航线，避免对空对象调用 String.replace 崩溃
  if (!device || !device.id) {
    return mockGisRouteData;
  }
  const deviceIndex = parseInt(device.id.replace('DEV', ''), 10) || 1;
  // 进度在 40-85% 之间变化
  const progress = (deviceIndex % 46) + 40;
  const totalNm = 5400;
  const sailed = Math.round(totalNm * progress / 100);
  const remaining = totalNm - sailed;
  const daysLeft = Math.max(1, Math.round(remaining / 180));

  return {
    routeId: `ROUTE-${device.deviceNo}`,
    routeName: '青岛 → 迪拜',
    points: [
      { name: '青岛', lat: 36.06, lng: 120.38, label: 'QD', isCurrent: false },
      { name: '当前位置', lat: 25.3, lng: 62.5, label: 'POS', isCurrent: true },
      { name: '迪拜', lat: 25.2, lng: 55.27, label: 'DXB', isCurrent: false },
    ],
    sailedNauticalMiles: sailed,
    remainingNauticalMiles: remaining,
    estimatedArrival: new Date(Date.now() + daysLeft * 86400000).toISOString(),
    progressPercent: progress,
  };
}

/** 根据设备和时间范围生成温度曲线数据点 */
export function generateDeviceReadingHistory(
  device: IoTDevice,
  timeRange: TimeRange,
): TempChartDataPoint[] {
  const rangeHours = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
  const now = Date.now();
  const cutoff = now - rangeHours * 3600000;

  // 过滤在时间范围内的读数
  const filtered = device.readingHistory.filter(
    (r) => new Date(r.timestamp).getTime() >= cutoff,
  );

  if (filtered.length === 0) {
    // 如果没有时间范围内的数据，使用最近的30个读数
    const recent = device.readingHistory.slice(-30);
    return recent.map((r) => ({
      time: formatBucketTime(new Date(r.timestamp).getTime(), rangeHours),
      temperature: r.temperature,
      upperThreshold: TEMP_UPPER,
      lowerThreshold: TEMP_LOWER,
    }));
  }

  // 24h：展示所有可用点
  if (timeRange === '24h') {
    // 取最多 96 个点
    const points = filtered.length > 96 ? filtered.slice(-96) : filtered;
    return points.map((r) => ({
      time: formatBucketTime(new Date(r.timestamp).getTime(), rangeHours),
      temperature: r.temperature,
      upperThreshold: TEMP_UPPER,
      lowerThreshold: TEMP_LOWER,
    }));
  }

  // 7d / 30d：分桶聚合
  const bucketSizeMs = timeRange === '7d' ? 2 * 3600000 : 6 * 3600000; // 2小时 or 6小时

  const buckets = new Map<number, { sum: number; count: number }>();
  filtered.forEach((r) => {
    const ts = new Date(r.timestamp).getTime();
    const bucketKey = Math.floor(ts / bucketSizeMs);
    const existing = buckets.get(bucketKey);
    if (existing) {
      existing.sum += r.temperature;
      existing.count += 1;
    } else {
      buckets.set(bucketKey, { sum: r.temperature, count: 1 });
    }
  });

  // 排序桶并计算均值
  const sorted = Array.from(buckets.entries()).sort(([a], [b]) => a - b);

  return sorted.map(([key, b]) => ({
    time: formatBucketTime(key * bucketSizeMs + bucketSizeMs / 2, rangeHours),
    temperature: +(b.sum / b.count).toFixed(1),
    upperThreshold: TEMP_UPPER,
    lowerThreshold: TEMP_LOWER,
  }));
}

/** 时间格式化（根据时间范围显示不同粒度） */
function formatBucketTime(timestamp: number, rangeHours: number): string {
  const d = new Date(timestamp);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  if (rangeHours <= 24) {
    return `${hours}:${minutes}`;
  }
  return `${month}/${day} ${hours}:${minutes}`;
}

/** 从设备数据派生温度指标 */
export function deriveTempIndicator(device: IoTDevice | null): {
  currentTemp: number;
  targetTemp: number;
  exceedMinutes: number;
  humidity: number;
} {
  if (!device) {
    return { currentTemp: 0, targetTemp: TEMP_TARGET, exceedMinutes: 0, humidity: 0 };
  }
  const last = device.lastReading;
  return {
    currentTemp: last.temperature,
    targetTemp: TEMP_TARGET,
    exceedMinutes: last.temperature > TEMP_UPPER ? Math.round(Math.random() * 30) : 0,
    humidity: last.humidity,
  };
}

/** 报警数据 */
export const mockIoTAlerts: IoTAlert[] = [
  {
    id: 'ALT001', deviceId: 'DEV001', containerId: 'CONT001',
    type: AlertType.TEMP_OUT_OF_RANGE, severity: AlertSeverity.WARNING,
    message: '温度超过阈值：当前 4.8°C，阈值 0-4°C',
    reading: { timestamp: '2025-07-02T08:30:00Z', temperature: 4.8, humidity: 65, co2: 420, shock: 0.1, lightExposure: 5 },
    acknowledged: false, acknowledgedBy: '', createdAt: '2025-07-02T08:30:00Z', resolvedAt: '',
  },
  {
    id: 'ALT002', deviceId: 'DEV003', containerId: 'CONT003',
    type: AlertType.SHOCK_DETECTED, severity: AlertSeverity.CRITICAL,
    message: '检测到剧烈震动：加速度 8.5g',
    reading: { timestamp: '2025-07-02T06:45:00Z', temperature: 2.5, humidity: 60, co2: 400, shock: 8.5, lightExposure: 3 },
    acknowledged: false, acknowledgedBy: '', createdAt: '2025-07-02T06:45:00Z', resolvedAt: '',
  },
  {
    id: 'ALT003', deviceId: 'DEV005', containerId: 'CONT005',
    type: AlertType.DEVICE_OFFLINE, severity: AlertSeverity.WARNING,
    message: '设备离线超过30分钟',
    reading: { timestamp: '2025-07-02T04:00:00Z', temperature: 3.1, humidity: 58, co2: 410, shock: 0.2, lightExposure: 4 },
    acknowledged: true, acknowledgedBy: '李强', createdAt: '2025-07-02T04:00:00Z', resolvedAt: '2025-07-02T04:45:00Z',
  },
  {
    id: 'ALT004', deviceId: 'DEV002', containerId: 'CONT002',
    type: AlertType.HUMIDITY_OUT_OF_RANGE, severity: AlertSeverity.INFO,
    message: '湿度偏高：当前 78%，阈值 50-70%',
    reading: { timestamp: '2025-07-02T02:15:00Z', temperature: 3.2, humidity: 78, co2: 430, shock: 0.1, lightExposure: 6 },
    acknowledged: true, acknowledgedBy: '王芳', createdAt: '2025-07-02T02:15:00Z', resolvedAt: '2025-07-02T03:30:00Z',
  },
  {
    id: 'ALT005', deviceId: 'DEV007', containerId: 'CONT007',
    type: AlertType.LOW_BATTERY, severity: AlertSeverity.INFO,
    message: '设备电量低于10%，当前 8%',
    reading: { timestamp: '2025-07-01T22:00:00Z', temperature: 3.5, humidity: 62, co2: 400, shock: 0.1, lightExposure: 2 },
    acknowledged: false, acknowledgedBy: '', createdAt: '2025-07-01T22:00:00Z', resolvedAt: '',
  },
  {
    id: 'ALT006', deviceId: 'DEV004', containerId: 'CONT004',
    type: AlertType.GEO_FENCE_BREACH, severity: AlertSeverity.CRITICAL,
    message: '集装箱偏离预定航线：偏离 12海里',
    reading: { timestamp: '2025-07-01T18:00:00Z', temperature: 2.8, humidity: 55, co2: 390, shock: 0.3, lightExposure: 8 },
    acknowledged: true, acknowledgedBy: '张伟', createdAt: '2025-07-01T18:00:00Z', resolvedAt: '2025-07-01T20:00:00Z',
  },
  {
    id: 'ALT007', deviceId: 'DEV008', containerId: 'CONT008',
    type: AlertType.TEMP_OUT_OF_RANGE, severity: AlertSeverity.CRITICAL,
    message: '温度严重超限：当前 6.2°C，阈值 0-4°C',
    reading: { timestamp: '2025-07-01T14:00:00Z', temperature: 6.2, humidity: 70, co2: 450, shock: 0.2, lightExposure: 7 },
    acknowledged: false, acknowledgedBy: '', createdAt: '2025-07-01T14:00:00Z', resolvedAt: '',
  },
  {
    id: 'ALT008', deviceId: 'DEV010', containerId: 'CONT010',
    type: AlertType.SHOCK_DETECTED, severity: AlertSeverity.WARNING,
    message: '检测到轻微震动：加速度 2.3g',
    reading: { timestamp: '2025-07-01T10:00:00Z', temperature: 3.0, humidity: 55, co2: 410, shock: 2.3, lightExposure: 4 },
    acknowledged: false, acknowledgedBy: '', createdAt: '2025-07-01T10:00:00Z', resolvedAt: '',
  },
  {
    id: 'ALT009', deviceId: 'DEV012', containerId: 'CONT012',
    type: AlertType.DEVICE_OFFLINE, severity: AlertSeverity.WARNING,
    message: '设备信号丢失超过1小时',
    reading: { timestamp: '2025-06-30T20:00:00Z', temperature: 2.9, humidity: 60, co2: 395, shock: 0.1, lightExposure: 3 },
    acknowledged: true, acknowledgedBy: '刘洋', createdAt: '2025-06-30T20:00:00Z', resolvedAt: '2025-06-30T21:30:00Z',
  },
  {
    id: 'ALT010', deviceId: 'DEV015', containerId: 'CONT015',
    type: AlertType.HUMIDITY_OUT_OF_RANGE, severity: AlertSeverity.WARNING,
    message: '湿度过低：当前 42%，阈值 50-70%',
    reading: { timestamp: '2025-06-30T15:00:00Z', temperature: 3.8, humidity: 42, co2: 380, shock: 0.1, lightExposure: 5 },
    acknowledged: true, acknowledgedBy: '陈静', createdAt: '2025-06-30T15:00:00Z', resolvedAt: '2025-06-30T16:00:00Z',
  },
  // 第三温度报警（D05 需要 3 个温度报警）
  {
    id: 'ALT011', deviceId: 'DEV006', containerId: 'CONT006',
    type: AlertType.TEMP_OUT_OF_RANGE, severity: AlertSeverity.CRITICAL,
    message: '温度严重超限：当前 5.7°C，阈值 0-4°C',
    reading: { timestamp: '2025-07-02T11:00:00Z', temperature: 5.7, humidity: 68, co2: 440, shock: 0.2, lightExposure: 6 },
    acknowledged: false, acknowledgedBy: '', createdAt: '2025-07-02T11:00:00Z', resolvedAt: '',
  },
  // 第二 LOW_BATTERY 报警（D05 需要 2 个电量预警）
  {
    id: 'ALT012', deviceId: 'DEV046', containerId: 'CONT047',
    type: AlertType.LOW_BATTERY, severity: AlertSeverity.WARNING,
    message: '设备电量不足：当前 12%',
    reading: { timestamp: '2025-07-02T05:30:00Z', temperature: 3.2, humidity: 55, co2: 400, shock: 0.1, lightExposure: 3 },
    acknowledged: false, acknowledgedBy: '', createdAt: '2025-07-02T05:30:00Z', resolvedAt: '',
  },
];
